/**
 * Game Timing Configuration
 * 
 * Centralizes all timeout and delay values used throughout the game.
 * Values can be overridden via environment variables in .env file.
 */

// Helper to parse env variable as integer with fallback
const parseEnvInt = (envValue, defaultValue) => {
    const parsed = parseInt(envValue, 10);
    return isNaN(parsed) ? defaultValue : parsed;
};

export const GAME_TIMINGS = {
    // Navigation between games
    navigation: {
        beforeWinnerModal: parseEnvInt(process.env.REACT_APP_TIMING_BEFORE_WINNER_MODAL, 2000),
        beforeNextGame: parseEnvInt(process.env.REACT_APP_TIMING_BEFORE_NEXT_GAME, 3000),
        roundWinnerDisplay: parseEnvInt(process.env.REACT_APP_TIMING_ROUND_WINNER_DISPLAY, 3000),
        afterCardCounting: parseEnvInt(process.env.REACT_APP_TIMING_AFTER_CARD_COUNTING, 7000),
        collectCardsAnimation: parseEnvInt(process.env.REACT_APP_TIMING_COLLECT_CARDS, 1500),
        beforeDealingNextGame: parseEnvInt(process.env.REACT_APP_TIMING_BEFORE_DEALING_NEXT, 1000),
        // Online báciga round/match-won modals: a short client-side beat on
        // top of the server's own roundOverDelayMs pause (bacigaGameSession.ts —
        // TODO.md "esperar unos segundos para que el modal se pueda ver").
        // The server already holds off ROUND_TRANSITION long enough for the
        // last scoring toast + card reveal to be read; this is just enough
        // to smooth over network jitter/animation queueing on top of that,
        // not a second full pacing wait.
        beforeOnlineBacigaWinnerModal: parseEnvInt(process.env.REACT_APP_TIMING_BEFORE_BACIGA_WINNER_MODAL, 1200),
    },

    // Card dealing
    dealing: {
        shufflingModal: parseEnvInt(process.env.REACT_APP_TIMING_SHUFFLING_MODAL, 2500),
        beforeInitialDeal: parseEnvInt(process.env.REACT_APP_TIMING_BEFORE_INITIAL_DEAL, 1000),
        beforeNewHand: parseEnvInt(process.env.REACT_APP_TIMING_BEFORE_NEW_HAND, 1000),
        beforeNewRound: parseEnvInt(process.env.REACT_APP_TIMING_BEFORE_NEW_ROUND, 2000),
        animationDuration: parseEnvInt(process.env.REACT_APP_TIMING_DEAL_ANIMATION, 400),
        betweenCards: parseEnvInt(process.env.REACT_APP_TIMING_DEAL_DELAY, 200),
    },

    // Ronda/Rondín declarations
    ronda: {
        betweenAnnouncements: parseEnvInt(process.env.REACT_APP_TIMING_RONDA_ANNOUNCEMENT, 800),
        afterPointsAwarded: parseEnvInt(process.env.REACT_APP_TIMING_AFTER_POINTS, 1000),
        // Window to allow the next player to play a contracaída before the collector sweeps
        caidaCounterWindow: parseEnvInt(process.env.REACT_APP_TIMING_RONDA_CAIDA_COUNTER, 1400),
    },

    // Envío (betting) phase
    envio: {
        aiDecisionDelay: parseEnvInt(process.env.REACT_APP_TIMING_AI_ENVIO_DECISION, 1000),
        transitionDelay: parseEnvInt(process.env.REACT_APP_TIMING_ENVIO_TRANSITION, 500),
        afterResolution: parseEnvInt(process.env.REACT_APP_TIMING_AFTER_ENVIO, 1500),
        playerActionDelay: parseEnvInt(process.env.REACT_APP_TIMING_PLAYER_ACTION, 800),
    },

    // AI behavior
    ai: {
        thinkingTime: parseEnvInt(process.env.REACT_APP_TIMING_AI_THINKING, 1500),
    },

    // Game start sequence
    gameStart: {
        beforeReveal: parseEnvInt(process.env.REACT_APP_TIMING_START_REVEAL, 1000),
    },

    // Báciga - Las Cosas victory sequence
    lasCosas: {
        // Wait for card dealing animation to complete before processing
        afterCardDeal: parseEnvInt(process.env.REACT_APP_TIMING_COSAS_AFTER_DEAL, 500),
        // Time to see the revealed covered card
        afterRevealCard: parseEnvInt(process.env.REACT_APP_TIMING_COSAS_AFTER_REVEAL, 800),
        // Time after binding wildcards for visual update
        afterBindWildcard: parseEnvInt(process.env.REACT_APP_TIMING_COSAS_AFTER_BIND, 500),
        // Time before revealing all cards after showing toast
        beforeRevealAll: parseEnvInt(process.env.REACT_APP_TIMING_COSAS_BEFORE_REVEAL_ALL, 1000),
        // Time to see the final result before ending hand
        afterVictory: parseEnvInt(process.env.REACT_APP_TIMING_COSAS_AFTER_VICTORY, 3000),
        // Duration of the "LAS COSAS" toast
        toastDuration: parseEnvInt(process.env.REACT_APP_TIMING_COSAS_TOAST_DURATION, 4000),
    },

    // Online báciga: Las31 score-counting phase (one 'scoreAnnounced'/
    // 'liguesWin' per seat, in turn)
    bacigaOnlineCounting: {
        // Beat before processing 'handEnded' itself, so the LAST seat's
        // score/ligues toast (which, unlike an AI seat's turn, has no
        // "thinking" pause in front of it when that seat is human) stays
        // readable instead of being covered immediately by the "handEnded"
        // banner and, shortly after, the next hand's own deal.
        afterLastScoreAnnouncement: parseEnvInt(process.env.REACT_APP_TIMING_BACIGA_AFTER_LAST_SCORE, 2500),
    },
};
